import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, ShieldAlert } from "lucide-react";
import { API, authHeader } from "@/lib/api";

export const BrokerPanel = ({ broker, onChanged }) => {
  const [form, setForm] = useState({ broker: "Exness", account_type: "demo", login: "", password: "", server: "", metaapi_token: "" });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(!broker);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/broker/connect`, form, { headers: authHeader() });
      toast.success(`Status: ${data.status}`, { description: data.note });
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error("Gagal menyimpan", { description: err.response?.data?.detail || "Periksa kembali isian." });
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    await axios.delete(`${API}/broker`, { headers: authHeader() });
    toast.success("Koneksi broker dihapus — mode dikembalikan ke Analysis Only");
    setEditing(true);
    onChanged?.();
  };

  const statusCls = broker?.status === "terhubung"
    ? "text-buy border-buy/50 bg-buy/10"
    : broker?.status === "gagal_koneksi"
    ? "text-sell border-sell/40 bg-sell/5"
    : "text-warn border-warn/40 bg-warn/5";

  return (
    <div className="max-w-2xl border border-white/10 bg-ink-surface p-6" data-testid="broker-panel">
      <div className="flex items-center justify-between">
        <div><h3 className="font-display text-lg font-bold text-white">Koneksi Broker MT5</h3><p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-sell">🔒 Live Trading Real — LOCKED</p></div>
        {broker && (
          <span className={`border px-2.5 py-1 font-mono text-[10px] font-bold uppercase ${statusCls}`} data-testid="broker-status">
            {broker.status.replace("_", " ")}
          </span>
        )}
      </div>

      {broker && !editing ? (
        <div className="mt-5 space-y-3 font-mono text-xs" data-testid="broker-summary">
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            {[["Broker", broker.broker], ["Tipe Akun", broker.account_type.toUpperCase()],
              ["Login", broker.login], ["Server", broker.server]].map(([k, v]) => (
              <div key={k} className="bg-ink px-3 py-2.5">
                <span className="text-mute-dim">{k}: </span><span className="text-white">{v}</span>
              </div>
            ))}
          </div>
          <p className="text-mute">{broker.note}</p>
          {broker.account_type === "real" && (
            <p className="flex items-start gap-2 border border-sell/40 bg-sell/5 p-3 text-sell">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              Akun REAL: eksekusi memakai uang asli. Mode Live tetap membutuhkan aktivasi eksplisit.
            </p>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={() => setEditing(true)} data-testid="broker-edit-button"
              className="border border-white/20 px-4 py-2 font-bold text-mute transition-colors hover:text-white">
              GANTI KREDENSIAL
            </button>
            <button onClick={remove} data-testid="broker-delete-button"
              className="flex items-center gap-2 border border-sell/40 px-4 py-2 font-bold text-sell transition-colors hover:bg-sell hover:text-white">
              <Trash2 size={13} /> HAPUS
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="broker-form">
          <div className="grid grid-cols-2 gap-2">
            {["demo", "real"].map((t) => (
              <button type="button" key={t} disabled onClick={() => setForm({ ...form, account_type: t })}
                data-testid={`broker-type-${t}`}
                className={`border p-3 text-left transition-colors duration-200 ${
                  form.account_type === t
                    ? t === "demo" ? "border-buy bg-buy/10" : "border-sell bg-sell/10"
                    : "border-white/15 hover:border-white/40"
                }`}>
                <span className={`font-mono text-xs font-bold uppercase ${
                  form.account_type === t ? (t === "demo" ? "text-buy" : "text-sell") : "text-white"}`}>
                  {t === "demo" ? "Akun Demo 🔒" : "Akun Real 🔒"}
                </span>
                <p className="mt-1 text-[10px] text-mute">
                  {t === "demo" ? "Uang virtual broker — disarankan dulu" : "Uang asli — risiko penuh"}
                </p>
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.broker} onChange={set("broker")} data-testid="broker-name-select"
              className="h-11 border border-white/15 bg-ink px-3 font-mono text-sm text-white">
              {["Exness", "XM", "HFM", "Lainnya"].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <input value={form.server} onChange={set("server")} required placeholder="server (mis. Exness-MT5Trial8)"
              data-testid="broker-server-input"
              className="h-11 border border-white/15 bg-ink px-3 font-mono text-sm outline-none focus:border-buy/60" />
            <input value={form.login} onChange={set("login")} required placeholder="nomor login MT5"
              data-testid="broker-login-input"
              className="h-11 border border-white/15 bg-ink px-3 font-mono text-sm outline-none focus:border-buy/60" />
            <input type="password" value={form.password} onChange={set("password")} required placeholder="password MT5"
              data-testid="broker-password-input"
              className="h-11 border border-white/15 bg-ink px-3 font-mono text-sm outline-none focus:border-buy/60" />
          </div>
          <input value={form.metaapi_token} onChange={set("metaapi_token")}
            placeholder="MetaAPI auth token (opsional — untuk koneksi bridge langsung)"
            data-testid="broker-metaapi-input"
            className="h-11 w-full border border-white/15 bg-ink px-3 font-mono text-xs outline-none focus:border-buy/60" />
          <p className="font-mono text-[10px] leading-relaxed text-mute-dim">
            &gt; server cloud tidak bisa menjalankan terminal MT5 langsung. bridge via metaapi.cloud:
            buat akun gratis → salin Auth Token → tempel di atas. tanpa token, kredensial tersimpan
            dan koneksi diuji setelah token ditambahkan.
          </p>
          <button type="submit" disabled={true} data-testid="broker-save-button"
            className="flex h-11 items-center gap-2 border border-sell/40 bg-sell/5 px-6 font-mono text-sm font-bold uppercase tracking-wider text-sell opacity-70">
            🔒 Broker Connection Locked
          </button>
        </form>
      )}
    </div>
  );
};
